/**
 Copyright (c) 2019 Attic Creation
 Form 3.2
 */
import { cloneElement, ReactElement, useRef } from "react";
import React from "react";
import FormErrorText from "./form-error-text";
import FormInput from "./form-input";
import FormCheckBox from "./form-checkbox";
import SingleSelect from "./form-single-select";


function FormWrapper({ children, className, initialValues = {}, onSubmit, onValueChange, autoComplete, style, reset, disableLoading = false }: { children: React.ReactNode; className?: string, initialValues?: Record<string, any>; onSubmit: (values: Record<string, any>) => Promise<any>; onValueChange?: (values: Record<string, string | boolean>) => void; autoComplete?: string | undefined; style?: React.CSSProperties; reset?: boolean; disableLoading?: boolean }) {

    // Loading state for form
    const [loading, setLoading] = React.useState<boolean>(false);

    // Flag to show / hide error texts and highlights
    const [showError, setShowError] = React.useState<boolean>(false);

    // Error object
    const [error, setError] = React.useState<Record<string, string | boolean>>({});

    // All values in an form retrieved based on the [name] prop from [InputField]
    const [values, setValues] = React.useState<Record<string, any>>(initialValues);

    // Form reference
    const formReference = useRef<HTMLFormElement>(null);

    // Submit is trigger on Form Submission which validates the fields based on given props.
    const submit = async (event: React.FormEvent<HTMLFormElement>) => {
        if (event && event.preventDefault) {
            event.preventDefault();
        }

        if (event && event.stopPropagation) {
            event.stopPropagation();
        }

        // Assuming all data is valid
        let validation = true;

        // Storing current error object
        let e: Record<string, string | boolean> = error;

        // Setting Show Error to false until validation is complete
        setShowError(false);

        let eventTarget = event.target as HTMLFormElement;

        if (eventTarget.elements) {
            let elementsLength: number = eventTarget.elements.length;
            for (let i = 0; i < elementsLength; i++) {
                let element = eventTarget.elements[i] as HTMLInputElement;
                if (element.hasAttribute("name")) {
                    switch (element.type) {
                        case "checkbox":
                            e[element.name] = !element.checked;
                            break;
                        case "email":
                            if (element.required && !element.value.trim()) {
                                e[element.name] = "empty";
                                validation = false;
                            } else if (!element.value.match(new RegExp(/^[a-zA-Z0-9.!#$%&’*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/))) {
                                e[element.name] = "empty";
                                e['text'] = "Please enter a valid Email Address."
                                validation = false;
                            } else {
                                e[element.name] = "";
                            }
                            break;
                        case "date":
                            if (element.required && !element.value) {
                                e[element.name] = "empty";
                                validation = false;
                            } else {
                                e[element.name] = "";
                            }
                            break;
                        case "tel":
                            if (element.required && !element.value.trim()) {
                                e[element.name] = "empty";
                                validation = false;
                            } else if (element.value.length !== 14) {
                                e[element.name] = "Please enter a valid Phone Number.";
                                validation = false;
                            } else {
                                e[element.name] = "";
                            }
                            break;
                        case "number":
                        case "text":
                        case "textarea":
                            if (element.required && !element.value.trim()) {
                                e[element.name] = "empty";
                                validation = false;
                            } else {
                                e[element.name] = "";
                            }
                            break;
                        case "password":
                            if (element.required && !element.value.trim()) {
                                e[element.name] = "empty";
                                validation = false;
                            } else if (element.name === "newPassword") {
                                if (element.value.trim().length < 6 ||
                                element.value.toString().match(RegExp(/\d/)) === null ||
                                element.value.toString().match(RegExp(/[ `!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?~]/)) === null
                                ) {
                                    e[element.name] = "empty";
                                    validation = false;
                                } else {
                                    e[element.name] = "";
                                }
                            } else {
                                e[element.name] = "";
                            }
                            break;
                        default:
                            break;
                    }
                }
            }
            await setError(e);
        }

        if (validation) {
            if (!disableLoading)
                await setLoading(true);
            await onSubmit(values)
                .then(async () => {
                    if (reset) {
                        if (formReference.current) formReference.current.reset();
                        let v: Record<string, string | boolean> = {};
                        for (let k in values) {
                            if (values.hasOwnProperty(k))
                                v[k] = "";
                        }
                        setValues(v);
                    }
                    await setLoading(false);
                    await setShowError(false);
                })
                .catch(async (err: Record<string, string | boolean> | string) => {
                    if (typeof err === "string") {
                        await setLoading(false);
                        await setShowError(true);
                        await setError({ text: err });
                    } else {
                        await setLoading(false);
                        await setShowError(true);
                        await setError(err);
                    }
                });
        } else {
            setLoading(false);
            setShowError(true);
        }
    };

    const handleChange = (event: React.FormEvent) => {
        let evenTarget = event.target as HTMLInputElement;
        setError({ ...error, [evenTarget.name]: "", text: "" });
        setValues({ ...values, [evenTarget.name]: evenTarget.type === "checkbox" ? evenTarget.checked : evenTarget.value });
        if (onValueChange) onValueChange({ ...values, [evenTarget.name]: evenTarget.type === "checkbox" ? evenTarget.checked : evenTarget.value });
    };

    const handleSelectChange = (val: string, name) => {
        setError({ ...error, [name]: "" });
        setValues({ ...values, [name]: val });
    };

    let processElements = (reactElements: Array<Exclude<React.ReactNode, boolean | null | undefined>>) => {
        reactElements.forEach((el, index) => {
            let element = el as ReactElement;
            if (element.type === FormInput)
                reactElements[index] = cloneElement(element, {
                    disabled: loading || element.props.disabled,
                    onChange: handleChange,
                    value: values[element.props.name] ?? "",
                    error: showError ? error[element.props.name] : "",
                    key: index,
                });
            else if (element.type === FormErrorText)
                reactElements[index] = cloneElement(element, {
                    error: error,
                    showError: showError,
                    key: index,
                });
            else if (element.type === SingleSelect)
                reactElements[index] = cloneElement(element, {
                    selectedValue: values[element.props.name],
                    setSelected: handleSelectChange,
                    error: values[element.props.name],
                    showError: showError,
                    key: index,
                });
            else if (element.type === FormCheckBox){
                reactElements[index] = cloneElement(element, {
                    disabled: loading || element.props.disabled,
                    onChange: handleChange,
                    value: values[element.props.name] ?? "",
                    key: index
                });
            }
            else {
                if (element.props && element.props.children) {
                    let possibleFields = React.Children.toArray(element.props.children);
                    if (possibleFields.length > 0) {
                        possibleFields = processElements(possibleFields);
                    }
                    reactElements[index] = cloneElement(element, {
                        children: possibleFields,
                    });
                }
            }
        });
        return reactElements;
    };

    let elements = React.Children.toArray(children);
    elements = processElements(elements);

    return (
        <form ref={formReference} onSubmit={submit} autoComplete={autoComplete} className={`p-0 w-[100%] flex flex-col ${className ?? ""}`} noValidate style={style}>
            {elements}
        </form>
    );
}

export default FormWrapper;
